'use client'

import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import Button from '@/components/ui/button'
import ThemeIcon from '@/components/ui/ThemeIcon'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import { cn } from '@/utilities/ui'

interface HistoryItem {
  year: string
  title: string
  description: string
}

type SectionSettings = {
  anchorId?: string
  backgroundShade?: 'white' | 'neutral-50' | 'neutral-100'
  isDarkMode?: boolean
  padding?: 'md' | 'lg'
}

type HeaderGroup = {
  infoTagText?: string
  title: string
  subtitle?: string
}

type HistoryBlockProps = {
  header: HeaderGroup
  items: HistoryItem[]
  settings?: SectionSettings
}

export default function HistoryBlock({ header, items, settings }: HistoryBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const visibleItems = useMemo(() => (expanded ? items : items.slice(0, 4)), [expanded, items])

  const normalizedSettings = normalizeSectionSettings(settings)

  return (
    <SectionWrapper settings={normalizedSettings}>
      {/* Header */}
      <SectionHeader
        infoTagText={header?.infoTagText ?? ''}
        title={header?.title ?? ''}
        subtitle={header?.subtitle}
        align="left"
        size="md"
        className="mb-0"
        subtitleClassName="subtitle-lg"
      />

      {/* Timeline */}
      <div className="relative mt-10 md:mt-12">
        {/* Center line for desktop/tablet, left line for mobile */}
        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 md:-translate-x-1/2 w-px bg-text-secondary/30 dark:bg-white/10" />

        <div className="space-y-12 md:space-y-14">
          {visibleItems.map((item, index) => {
            const isLeft = index % 2 === 0

            return (
              <motion.div
                key={`${item.year}-${item.title}`}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-8 items-start"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: index * 0.12,
                }}
              >
                {/* Left content - only on desktop/tablet */}
                <div
                  className={cn(
                    'hidden md:block md:col-start-1',
                    isLeft ? 'md:order-1' : 'md:order-1',
                  )}
                >
                  {isLeft && (
                    <div className="px-5 md:px-6 text-right">
                      <div className="flex items-center gap-3 text-brand justify-end">
                        <span className="text-lg font-bold">{item.year}</span>
                      </div>
                      <h3 className=" title-md uppercase text-primary">{item.title}</h3>
                      <p className="mt-2 body-base text-secondary">{item.description}</p>
                    </div>
                  )}
                </div>

                {/* Marker - left side on mobile, center on desktop/tablet */}
                <div className="col-start-1 md:col-start-2 flex justify-center">
                  <div className="relative flex items-center justify-center w-12 h-12 rounded-full bg-background border border-text-secondary/40 dark:border-white/20">
                    <ThemeIcon size="size-6" />
                  </div>
                </div>

                {/* Content - always on right side for mobile, alternating for desktop/tablet */}
                <div
                  className={cn('col-start-2 md:col-start-3', isLeft ? 'md:order-1' : 'md:order-1')}
                >
                  {/* Mobile content - always left aligned */}
                  <div className="block md:hidden">
                    <div className="flex items-center gap-3 text-brand">
                      <span className="text-lg font-bold">{item.year}</span>
                    </div>
                    <h3 className=" title-md uppercase text-primary">{item.title}</h3>
                    <p className="mt-2 body-base text-secondary">{item.description}</p>
                  </div>

                  {/* Desktop content - alternating sides */}
                  {!isLeft && (
                    <div className="hidden md:block px-5 md:px-6">
                      <div className="flex items-center gap-3 text-brand">
                        <span className="text-lg font-bold">{item.year}</span>
                      </div>
                      <h3 className=" title-md uppercase text-primary">{item.title}</h3>
                      <p className="mt-2 body-base text-secondary">{item.description}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* Expand button placed after the timeline container to avoid overlay by the center line */}
      {!expanded && items.length > 4 && (
        <div className="mt-10 md:mt-12 flex justify-center">
          <Button
            text="Mehr erfahren"
            color="brand"
            variant="primary"
            size="md"
            onClick={() => setExpanded(true)}
          />
        </div>
      )}
    </SectionWrapper>
  )
}
